import type { TrialWizardData } from '../api/trials';
import { deepEquals } from '../utils/deep_equals';

// Fields of the wizard payload that are NOT part of the library structure: trial metadata/state,
// the plots (which carry their own save flags), and client-only / read-only fields. Everything
// else is treated as "library structure" for the purposes of deciding whether to re-derive.
export const NON_STRUCTURAL_WIZARD_FIELDS: (keyof TrialWizardData)[] = [
  'trial',
  'plots',
  'plot_guides',
  'save_plots',
  'save_plot_guides',
  'need_rederive',
  'copy_dashboard_from_id',
  'default_header_order',
  'available_headers',
  'is_dirty',
  // saved on every wizard save, independently of the library re-derivation
  'input_applications',
];

// For library trials the backend re-derives the whole structure (datasets, scheduled
// visits, treatments) on every wizard save, which is destructive and expensive. Only ask it to
// re-derive when the library structure actually changed; a pure metadata/state change (e.g.
// archiving a template on the Activation step) must not trigger it. Manual mode and brand-new
// trials always derive.
//
// The comparison is conservative: any field outside NON_STRUCTURAL_WIZARD_FIELDS that differs from
// the last saved payload re-derives, so an unrecognised structural change is never skipped — we
// only return false when nothing structural differs.
export function shouldRederiveLibraryStructure(
  data: TrialWizardData,
  lastSavedData: TrialWizardData | null,
  isExistingTrial: boolean,
  didUpdateTrait: boolean
): boolean {
  if (data.edit_mode !== 'library') {
    return true; // the backend ignores need_rederive outside library mode
  }
  if (!isExistingTrial || !lastSavedData) {
    return true; // a new trial must always be derived
  }
  if (didUpdateTrait) {
    // A trait edited from the wizard is saved through its own endpoint, which already
    // re-derived the trial server-side and recreated the treatments with new ids
    // (MeasurementMetaDetailSerializer._update → lock_and_rederive_trial). The wizard
    // payload still carries the old ids, so the diff below can't see the change; the
    // save must request a re-derivation or the backend rejects the stale treatment ids.
    return true;
  }

  const structuralOnly = (d: TrialWizardData) => {
    const copy: Partial<TrialWizardData> = { ...d };
    for (const key of NON_STRUCTURAL_WIZARD_FIELDS) {
      delete copy[key];
    }
    return copy;
  };

  return !deepEquals(structuralOnly(data), structuralOnly(lastSavedData));
}
